/**
 * TemplateSelectionModal - Two-level bulk generate modal
 *
 * Level 1: "Custom" — All parameters empty with placeholders + info tooltips.
 *          Sub-toggle for Full Draft vs Outline Only.
 * Level 2: "Auto"   — Pre-filled smart defaults, 1-click generate.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Select, Space, Button, Spin, Checkbox, InputNumber, Segmented, Tooltip } from 'antd';
import {
  FileText, Sparkles, ChevronRight, ChevronDown,
  Database, BookOpen, PenTool, Zap, Info, Globe, Upload,
} from 'lucide-react';
import { useBlogTemplates, useRecommendedTemplates } from '@/hooks/useBlogTemplates';
import { RecipeChooser, type RecipeOption } from '@/components/blog-templates/picker/RecipeChooser';
import { contentPlannerService } from '@/services/contentPlannerService';
import { useTenantStore } from '@/stores/useTenantStore';
import { useResizableAntdModal } from '@/hooks/useResizableAntdModal';
import { cn } from '@/lib/utils';
import {
  getKeywordGrade,
  GRADE_COLORS,
  FRUIT_TIER_LABELS,
  FRUIT_TIER_COLORS,
} from '@/types/contentPlanner';
import type { BlogTemplateSummary } from '@/services/blogTemplatesService';
import type { ClusterSummary, FruitTier } from '@/types/contentPlanner';
import { useTopicalMaps } from '@/hooks/useContentPlannerAPI';
import { useBrandBible } from '@/hooks/useBrandBible';
import { useContentList } from '@/hooks/useBlogCreationAPI';
import { ResearchSessionsDrawer } from '@/components/content-planner/ResearchSessionsDrawer';
import {
  getWordPressPublishMode,
  shouldExportToGdocs,
  type PublishDestination,
  type WordPressPublishMode,
} from '@/components/content-planner/ContentPlannerWizard';

// ---------------------------------------------------------------------------
// Exported types
// ---------------------------------------------------------------------------

export type AssistanceLevel = 'guide' | 'suggestion' | 'fully_automated';

export interface AutoScheduleSettings {
  templateId: string | null;
  modelOverride: string | null;
  imageModelOverride: string | null;
  autoPublish: boolean;
  wordpressPublishMode: WordPressPublishMode;
  exportToGdocs: boolean;
  clusterIds: string[];
  numPosts: number;
  numDays: number;
  assistanceLevel: AssistanceLevel;
}

// Internal types
type ModalLevel = 'custom' | 'auto';
type ContentMode = 'full_draft' | 'outline_only';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface TemplateSelectionModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: (settings: AutoScheduleSettings) => void;
  loading?: boolean;
}

// ---------------------------------------------------------------------------
// Helper: check if cluster is selectable for content generation
// ---------------------------------------------------------------------------

function isClusterSelectable(cluster: ClusterSummary): boolean {
  // Skip clusters that already have content generated
  if (cluster.content_id) return false;
  return cluster.status === 'new' || cluster.status === 'approved';
}

// ---------------------------------------------------------------------------
// Helper: Deduplicate semantically similar keywords
// ---------------------------------------------------------------------------

const STOP_WORDS = new Set([
  // Grammar
  'a','an','the','for','to','in','on','of','and','or','is','are','was','were',
  'my','your','how','what','why','when','where','which','do','does','can',
  // Intent modifiers (don't differentiate content)
  'near','me','you','best','top','vs','guide','complete','ultimate',
  'buy','sale','used','new','cheap','affordable','price','cost','prices',
  'review','reviews','rated','find','get','hire','rent','rental',
]);

function tokenize(keyword: string): Set<string> {
  return new Set(
    keyword.toLowerCase().split(/\s+/).filter(w => w.length > 1 && !STOP_WORDS.has(w))
  );
}

function isSubset(smaller: Set<string>, larger: Set<string>): boolean {
  for (const token of smaller) {
    if (!larger.has(token)) return false;
  }
  return true;
}

/**
 * Check if two token sets are semantically similar (Jaccard > 0.6 or near-subset).
 */
function isSimilarTokens(a: Set<string>, b: Set<string>): boolean {
  if (a.size === 0 || b.size === 0) return false;

  // Jaccard similarity
  let intersection = 0;
  for (const t of a) { if (b.has(t)) intersection++; }
  const union = new Set([...a, ...b]).size;
  if (union > 0 && intersection / union > 0.6) return true;

  // Subset check: only when the smaller set has ≥2 tokens AND the
  // larger set adds at most 1 extra token (otherwise the extra terms
  // likely represent a genuinely different topic, e.g. "3 pt hitch log splitters").
  const [smaller, larger] = a.size <= b.size ? [a, b] : [b, a];
  if (smaller.size >= 2 && isSubset(smaller, larger) && larger.size - smaller.size <= 1) return true;

  return false;
}

/**
 * Greedy dedup: iterate sorted clusters (best first), skip any whose keyword
 * tokens overlap too much with an already-accepted cluster OR with existing
 * content keywords (already-generated blog posts).
 *
 * Two keywords are considered duplicates when:
 * - Jaccard similarity > 0.6, OR
 * - One keyword's meaningful tokens are a subset of the other's (min 2 tokens)
 */
function deduplicateClusters(
  clusters: ClusterSummary[],
  existingKeywords: string[] = [],
): ClusterSummary[] {
  // Pre-seed with existing content keywords so new selections don't overlap
  const knownTokenSets: Set<string>[] = existingKeywords
    .map(tokenize)
    .filter(ts => ts.size > 0);

  const accepted: { cluster: ClusterSummary; tokens: Set<string> }[] = [];

  for (const cluster of clusters) {
    const tokens = tokenize(cluster.name);
    if (tokens.size === 0) {
      accepted.push({ cluster, tokens });
      continue;
    }

    // Check against existing content keywords
    const matchesExisting = knownTokenSets.some(existing => isSimilarTokens(tokens, existing));
    if (matchesExisting) continue;

    // Check against already-accepted clusters in this batch
    const matchesBatch = accepted.some(({ tokens: existing }) => isSimilarTokens(tokens, existing));
    if (matchesBatch) continue;

    accepted.push({ cluster, tokens });
    knownTokenSets.push(tokens);
  }

  return accepted.map(a => a.cluster);
}

// ---------------------------------------------------------------------------
// Helper: Parameter label with info tooltip
// ---------------------------------------------------------------------------

function ParamLabel({ label, tooltip }: { label: string; tooltip: string }) {
  return (
    <label className="flex items-center gap-1.5 text-sm font-medium text-foreground">
      {label}
      <Tooltip title={tooltip}>
        <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
      </Tooltip>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: Expandable map session row with lazy-loaded clusters
// ---------------------------------------------------------------------------

interface MapSessionRowProps {
  mapId: string;
  seedKeyword: string;
  totalClusters: number;
  totalKeywords: number;
  totalVolume: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  selectedIds: Set<string>;
  onToggleCluster: (clusterId: string, clusterName: string) => void;
  onSelectAllInMap: (clusters: ClusterSummary[]) => void;
  onDeselectAllInMap: (clusterIds: string[]) => void;
}

function MapSessionRow({
  mapId,
  seedKeyword,
  totalClusters,
  totalKeywords,
  totalVolume,
  isExpanded,
  onToggleExpand,
  selectedIds,
  onToggleCluster,
  onSelectAllInMap,
  onDeselectAllInMap,
}: MapSessionRowProps) {
  const tenantId = useTenantStore(state => state.currentTenant);

  const { data: mapDetail, isLoading } = useQuery({
    queryKey: ['topical-map', tenantId, mapId],
    queryFn: () => contentPlannerService.getMap(mapId),
    enabled: isExpanded && !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  const clusters = mapDetail?.clusters ?? [];
  const selectableClusters = clusters.filter(isClusterSelectable);
  const selectedInMap = selectableClusters.filter(c => selectedIds.has(c.id));
  const allSelected = selectableClusters.length > 0 && selectedInMap.length === selectableClusters.length;
  const someSelected = selectedInMap.length > 0 && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      onDeselectAllInMap(selectableClusters.map(c => c.id));
    } else {
      onSelectAllInMap(selectableClusters);
    }
  };

  return (
    <div className="border-b last:border-b-0">
      <div
        onClick={onToggleExpand}
        className="flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-muted transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}
        <span className="font-medium text-sm text-foreground flex-1 truncate">
          {seedKeyword}
        </span>
        <span className="text-xs text-muted-foreground flex-shrink-0">
          {totalClusters} clusters
        </span>
        <span className="text-xs text-muted-foreground flex-shrink-0 hidden sm:inline">
          {totalKeywords} kws
        </span>
        <span className="text-xs font-medium text-muted-foreground flex-shrink-0">
          {totalVolume?.toLocaleString()} vol
        </span>
        {selectedInMap.length > 0 && (
          <span className="text-xs font-medium text-teal bg-teal-light px-1.5 py-0.5 rounded flex-shrink-0">
            {selectedInMap.length} selected
          </span>
        )}
      </div>

      {isExpanded && (
        <div className="bg-muted/50 border-t px-3 pb-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Spin size="small" />
              <span className="ml-2 text-xs text-muted-foreground">Loading clusters...</span>
            </div>
          ) : selectableClusters.length === 0 ? (
            <div className="text-center py-3 text-xs text-muted-foreground">
              No available clusters (all in progress or published)
            </div>
          ) : (
            <>
              <div className="flex items-center py-1.5 mb-1 border-b border-border">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleSelectAll}
                >
                  <span className="text-xs text-muted-foreground">
                    Select all ({selectableClusters.length})
                  </span>
                </Checkbox>
                <span className="ml-auto text-[10px] text-muted-foreground flex gap-3">
                  <span className="w-14 text-right">Vol</span>
                  <span className="w-12 text-right">KD</span>
                  <span className="w-16 text-right">Opp</span>
                </span>
              </div>

              <div className="max-h-[200px] overflow-y-auto">
                {selectableClusters.map(cluster => {
                  const isSelected = selectedIds.has(cluster.id);
                  const grade = cluster.avg_difficulty != null
                    ? getKeywordGrade(cluster.avg_difficulty)
                    : null;

                  return (
                    <div
                      key={cluster.id}
                      className={cn(
                        'flex items-center gap-1 py-1 px-1 rounded transition-colors cursor-pointer',
                        isSelected ? 'bg-teal-light/50' : 'hover:bg-muted/50',
                      )}
                      onClick={() => onToggleCluster(cluster.id, cluster.name)}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={() => onToggleCluster(cluster.id, cluster.name)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="text-xs flex-1 truncate text-foreground">
                        {cluster.name}
                      </span>
                      <span className="text-xs text-muted-foreground w-14 text-right flex-shrink-0">
                        {cluster.total_volume?.toLocaleString()}
                      </span>
                      <span className="text-xs text-muted-foreground w-12 text-right flex-shrink-0 inline-flex items-center justify-end gap-0.5">
                        {cluster.avg_difficulty?.toFixed(0) ?? '-'}
                        {grade && (
                          <span className={cn(
                            'px-0.5 rounded text-[9px] font-bold',
                            GRADE_COLORS[grade],
                          )}>
                            {grade}
                          </span>
                        )}
                      </span>
                      <span className="flex items-center gap-1 flex-shrink-0 w-16 justify-end">
                        <span className="text-xs tabular-nums">
                          {cluster.opportunity_score}
                        </span>
                        {cluster.fruit_tier && (
                          <span className={cn(
                            'px-1 py-0 rounded text-[9px] font-medium border',
                            FRUIT_TIER_COLORS[cluster.fruit_tier as FruitTier],
                          )}>
                            {FRUIT_TIER_LABELS[cluster.fruit_tier as FruitTier]}
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Publish Destination Selector (reused in Custom + Auto)
// ---------------------------------------------------------------------------

function PublishDestinationSelector({
  value,
  onChange,
}: {
  value: PublishDestination;
  onChange: (v: PublishDestination) => void;
}) {
  const options = [
    { value: 'none' as const, label: 'App Draft Only', icon: <FileText size={14} /> },
    { value: 'wp_draft' as const, label: 'WordPress Draft', icon: <Globe size={14} /> },
    { value: 'gdocs' as const, label: 'Google Doc Only', icon: <Upload size={14} /> },
    { value: 'wp_draft_gdocs' as const, label: 'WP Draft + Doc', icon: <Globe size={14} /> },
    { value: 'wp_publish' as const, label: 'Publish to WP', icon: <Globe size={14} /> },
    { value: 'wp_publish_gdocs' as const, label: 'Publish + Doc', icon: <Globe size={14} /> },
  ];

  return (
    <div className="grid grid-cols-2 gap-1.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs transition-colors text-left ${
            value === opt.value
              ? 'border-teal bg-teal-light text-teal-dark font-medium'
              : 'border-border bg-card text-muted-foreground hover:border-teal/50'
          } cursor-pointer`}
        >
          {opt.icon}
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function TemplateSelectionModal({
  open,
  onCancel,
  onConfirm,
  loading = false,
}: TemplateSelectionModalProps) {
  const tenantId = useTenantStore(state => state.currentTenant);
  const queryClient = useQueryClient();

  // Keyword selection state — Map<clusterId, clusterName> for display
  const [selectedClusters, setSelectedClusters] = useState<Map<string, string>>(new Map());
  const selectedClusterIds = useMemo(() => new Set(selectedClusters.keys()), [selectedClusters]);
  const [expandedMapId, setExpandedMapId] = useState<string | null>(null);

  // Research drawer state
  const [showResearchDrawer, setShowResearchDrawer] = useState(false);

  // Two-level mode (internal)
  const [modalLevel, setModalLevel] = useState<ModalLevel>('custom');
  const [contentMode, setContentMode] = useState<ContentMode>('full_draft');

  // Schedule: posts per day × num days
  const [postsPerDay, setPostsPerDay] = useState<number | null>(null);
  const [numDays, setNumDays] = useState<number | null>(null);

  // Template settings
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [publishDestination, setPublishDestination] = useState<PublishDestination>('none');

  // Fetch ALL topical maps (drawer needs in-progress + completed)
  const { data: allMapsData, refetch: refetchMaps } = useTopicalMaps(
    open ? tenantId : null,
    { limit: 100 },
  );
  const allMaps = allMapsData?.maps ?? [];
  const completedMaps = allMaps.filter(m => m.status === 'completed');

  // Brand Bible for AI Suggest in research drawer
  const { data: brandBible } = useBrandBible(open ? tenantId : null);

  // Existing content keywords — used to dedup against already-generated articles
  const { data: contentListData } = useContentList();
  const existingKeywords = useMemo(() => {
    const items = contentListData?.items;
    if (!items || items.length === 0) return [];
    return items
      .filter(item => !item.is_deleted)
      .map(item => item.keyword)
      .filter(Boolean);
  }, [contentListData?.items]);

  // Fetch auto templates
  const { data: templates, isLoading: templatesLoading } = useBlogTemplates({
    template_type: 'auto',
  });
  const autoTemplates = templates?.templates ?? [];
  // R5 ruled source for Recommended: list_recommended endpoint. While the
  // query is loading/errors, recommendedIds stays undefined and the chooser's
  // client-side heuristic is the ruled fallback (endpoint resilience).
  const { data: recommendedTemplates } = useRecommendedTemplates();
  const recommendedIds = useMemo(
    () => recommendedTemplates?.map((t) => t.id),
    [recommendedTemplates],
  );

  // Computed: effective values with defaults
  const effectivePostsPerDay = postsPerDay ?? (modalLevel === 'auto' ? 2 : 1);
  const effectiveNumDays = numDays ?? 5;
  const totalPosts = effectivePostsPerDay * effectiveNumDays;

  // ── Cluster selection handlers ──────────────────────────────────────────

  const handleToggleCluster = useCallback((clusterId: string, clusterName: string) => {
    setSelectedClusters(prev => {
      const next = new Map(prev);
      if (next.has(clusterId)) next.delete(clusterId);
      else next.set(clusterId, clusterName);
      return next;
    });
  }, []);

  const handleSelectAllInMap = useCallback((clusters: ClusterSummary[]) => {
    setSelectedClusters(prev => {
      const next = new Map(prev);
      clusters.forEach(c => next.set(c.id, c.name));
      return next;
    });
  }, []);

  const handleDeselectAllInMap = useCallback((clusterIds: string[]) => {
    setSelectedClusters(prev => {
      const next = new Map(prev);
      clusterIds.forEach(id => next.delete(id));
      return next;
    });
  }, []);

  // Auto-select best clusters when in Auto mode
  const [autoSelectLoading, setAutoSelectLoading] = useState(false);
  // Cache of all selectable clusters sorted by opportunity score (fetched once)
  const [cachedSortedClusters, setCachedSortedClusters] = useState<ClusterSummary[]>([]);

  // Fetch all selectable clusters once when entering Auto mode
  useEffect(() => {
    if (modalLevel !== 'auto' || !open || completedMaps.length === 0) return;
    // Already fetched
    if (cachedSortedClusters.length > 0) return;

    let cancelled = false;
    const fetchAll = async () => {
      setAutoSelectLoading(true);
      try {
        const allClusters: ClusterSummary[] = [];
        for (const map of completedMaps) {
          const detail = await contentPlannerService.getMap(map.map_id);
          if (cancelled) return;
          const selectable = (detail.clusters ?? []).filter(isClusterSelectable);
          allClusters.push(...selectable);
        }
        if (cancelled) return;

        const sorted = allClusters.sort(
          (a, b) => (b.opportunity_score ?? 0) - (a.opportunity_score ?? 0),
        );
        // Remove semantically similar keywords to prevent duplicate content
        // Also filters against existing content keywords (already-generated articles)
        const deduped = deduplicateClusters(sorted, existingKeywords);
        setCachedSortedClusters(deduped);
      } catch (err) {
        console.error('[AUTO_SELECT] Failed to fetch clusters:', err);
      } finally {
        if (!cancelled) setAutoSelectLoading(false);
      }
    };

    fetchAll();
    return () => { cancelled = true; };
  }, [modalLevel, open, completedMaps.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-slice selection when totalPosts changes or clusters are fetched
  useEffect(() => {
    if (modalLevel !== 'auto' || cachedSortedClusters.length === 0) return;

    const topN = cachedSortedClusters.slice(0, totalPosts);
    const next = new Map<string, string>();
    topN.forEach(c => next.set(c.id, c.name));
    setSelectedClusters(next);
  }, [modalLevel, cachedSortedClusters, totalPosts]);

  // Map internal state → external AutoScheduleSettings
  const handleConfirm = () => {
    const assistanceLevel: AssistanceLevel =
      modalLevel === 'auto'
        ? 'fully_automated'
        : contentMode === 'outline_only'
          ? 'guide'
          : 'suggestion';

    onConfirm({
      templateId: selectedTemplateId,
      modelOverride: null,
      imageModelOverride: null,
      autoPublish: getWordPressPublishMode(publishDestination) === 'publish',
      wordpressPublishMode: getWordPressPublishMode(publishDestination),
      exportToGdocs: shouldExportToGdocs(publishDestination),
      clusterIds: Array.from(selectedClusters.keys()),
      numPosts: totalPosts,
      numDays: effectiveNumDays,
      assistanceLevel,
    });
  };

  const handleCancel = () => {
    setSelectedClusters(new Map());
    setExpandedMapId(null);
    setShowResearchDrawer(false);
    setSelectedTemplateId(null);
    setPublishDestination('none');
    setModalLevel('custom');
    setContentMode('full_draft');
    setPostsPerDay(null);
    setNumDays(null);
    setCachedSortedClusters([]);
    onCancel();
  };

  // ── Select options ──────────────────────────────────────────────────────

  const templateOptions = [
    {
      value: '',
      label: (
        <span className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-500 dark:text-amber-400" />
          Default (Auto-select)
        </span>
      ),
    },
    ...autoTemplates.map((template: BlogTemplateSummary) => ({
      value: template.id,
      label: (
        <span className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-muted-foreground" />
          {template.name}
          {template.description && (
            <span className="text-xs text-muted-foreground ml-1">
              - {template.description.slice(0, 40)}
              {template.description.length > 40 ? '...' : ''}
            </span>
          )}
        </span>
      ),
    })),
  ];

  const selectedCount = selectedClusters.size;

  const isOutline = contentMode === 'outline_only' && modalLevel === 'custom';
  const postLabel = isOutline ? 'Outline' : 'Post';
  const postsLabel = isOutline ? 'Outlines' : 'Posts';

  const resizable = useResizableAntdModal({
    storageKey: 'template-selection-modal-size-v1',
    defaultWidth: 640,
    defaultHeight: 520,
    minHeight: 280,
  });

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <>
    <Modal
      title="Generate Blog Posts"
      open={open}
      zIndex={showResearchDrawer ? 40 : undefined}
      onCancel={handleCancel}
      maskClosable={!loading}
      closable={!loading}
      footer={
        <Space>
          <Button onClick={handleCancel} disabled={loading}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleConfirm}
            loading={loading}
            disabled={selectedCount === 0 || autoSelectLoading}
            icon={<Sparkles className="w-4 h-4" />}
          >
            Generate {modalLevel === 'auto' ? totalPosts : selectedCount} {(modalLevel === 'auto' ? totalPosts : selectedCount) === 1 ? postLabel : postsLabel}
          </Button>
        </Space>
      }
      {...resizable}
      destroyOnHidden
    >
      <div className="space-y-3 py-1">
        {/* Selected keywords display */}
        {selectedCount > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">
                {selectedCount} {selectedCount === 1 ? 'keyword' : 'keywords'} selected
              </span>
              <button
                type="button"
                onClick={() => setSelectedClusters(new Map())}
                className="text-xs text-muted-foreground hover:text-muted-foreground transition-colors"
              >
                Clear all
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-14 overflow-y-auto">
              {Array.from(selectedClusters.entries()).map(([id, name]) => (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-light text-xs text-teal-dark border border-teal/20"
                >
                  {name}
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedClusters(prev => {
                        const next = new Map(prev);
                        next.delete(id);
                        return next;
                      })
                    }
                    className="hover:text-teal ml-0.5 leading-none"
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ── Mode Selector (2 levels) ───────────────────────────── */}
        <div className="space-y-2">
          <Segmented
            block
            value={modalLevel}
            onChange={(val) => {
              const level = val as ModalLevel;
              setModalLevel(level);
              if (level === 'auto') {
                // Smart defaults for Auto
                if (postsPerDay === null) setPostsPerDay(2);
                if (numDays === null) setNumDays(5);
                if (publishDestination === 'none') setPublishDestination('wp_publish');
              }
            }}
            options={[
              {
                value: 'custom',
                label: (
                  <div className="flex items-center gap-1.5 py-1">
                    <PenTool className="w-3.5 h-3.5" />
                    <span>Custom</span>
                  </div>
                ),
              },
              {
                value: 'auto',
                label: (
                  <div className="flex items-center gap-1.5 py-1">
                    <Zap className="w-3.5 h-3.5" />
                    <span>Auto</span>
                  </div>
                ),
              },
            ]}
          />
          <p className="text-xs text-muted-foreground">
            {modalLevel === 'custom'
              ? 'Configure each parameter. Use Tab to accept defaults.'
              : 'Pre-filled with smart defaults. Ready to generate with one click.'}
          </p>
        </div>

        {/* ════════════════════════════════════════════════════════════ */}
        {/*  AUTO MODE: Summary card with inline editing                */}
        {/* ════════════════════════════════════════════════════════════ */}
        {modalLevel === 'auto' && (
          <>
            <div className="rounded-lg border border-teal/30 bg-teal-light/30 p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-teal-dark">
                <Zap className="w-4 h-4" />
                {autoSelectLoading ? 'Selecting best keywords...' : 'Ready to Generate'}
                {autoSelectLoading && <Spin size="small" />}
              </div>
              {selectedCount > 0 && !autoSelectLoading && (
                <p className="text-xs text-teal-dark/70">
                  {selectedCount} keyword{selectedCount !== 1 ? 's' : ''} auto-selected by opportunity score
                </p>
              )}

              {/* Schedule */}
              <div className="space-y-1">
                <ParamLabel label="Schedule" tooltip="All posts generate immediately. The schedule controls when they get published (spread across Mon/Wed/Fri)." />
                <div className="flex items-center gap-2">
                  <InputNumber
                    min={1}
                    max={10}
                    value={effectivePostsPerDay}
                    onChange={(v) => setPostsPerDay(v ?? 2)}
                    className="w-16"
                    size="small"
                  />
                  <span className="text-sm text-muted-foreground">posts/day for</span>
                  <InputNumber
                    min={1}
                    max={90}
                    value={effectiveNumDays}
                    onChange={(v) => setNumDays(v ?? 5)}
                    className="w-16"
                    size="small"
                  />
                  <span className="text-sm text-muted-foreground">days</span>
                  <span className="text-xs text-muted-foreground ml-auto">{totalPosts} total</span>
                </div>
              </div>

              {/* Template */}
              <div className="space-y-1">
                <ParamLabel label="Template" tooltip="Templates define blog structure (headings, sections, CTA). Default lets AI choose the best structure per topic." />
                <RecipeChooser
                  templates={autoTemplates as unknown as RecipeOption[]}
                  recentlyUsedIds={templates?.recently_used ?? []}
                  recommendedIds={recommendedIds}
                  selectedRecipeId={selectedTemplateId}
                  onSelect={setSelectedTemplateId}
                  onConfirm={(id) => setSelectedTemplateId(id)}
                  selectMode
                  className="p-0"
                />
              </div>

              {/* Publish Destination */}
              <div className="space-y-1">
                <ParamLabel label="Publish Destination" tooltip="Choose where posts are published on their scheduled date. Draft keeps them in the editor for manual review. WP and Docs publish automatically on schedule." />
                <PublishDestinationSelector
                  value={publishDestination}
                  onChange={setPublishDestination}
                />
              </div>
            </div>

          </>
        )}

        {/* ════════════════════════════════════════════════════════════ */}
        {/*  CUSTOM MODE: All parameters with placeholders + tooltips   */}
        {/* ════════════════════════════════════════════════════════════ */}
        {modalLevel === 'custom' && (
          <>
            {/* Content Mode Sub-Toggle */}
            <div className="space-y-1">
              <ParamLabel label="Content Mode" tooltip="Full Draft generates complete AI-written articles. Outline Only creates headings and section briefs for you to write." />
              <Segmented
                size="small"
                value={contentMode}
                onChange={(val) => setContentMode(val as ContentMode)}
                options={[
                  {
                    value: 'full_draft',
                    label: (
                      <div className="flex items-center gap-1.5">
                        <PenTool className="w-3 h-3" />
                        <span>Full Draft</span>
                      </div>
                    ),
                  },
                  {
                    value: 'outline_only',
                    label: (
                      <div className="flex items-center gap-1.5">
                        <BookOpen className="w-3 h-3" />
                        <span>Outline Only</span>
                      </div>
                    ),
                  },
                ]}
              />
            </div>

            {/* Schedule */}
            <div className="space-y-1">
              <ParamLabel label="Schedule" tooltip="All posts generate immediately. The schedule controls when they get published (spread across Mon/Wed/Fri)." />
              <div className="flex items-center gap-2">
                <InputNumber
                  min={1}
                  max={10}
                  value={postsPerDay}
                  placeholder="1"
                  onChange={(v) => setPostsPerDay(v)}
                  className="w-16"
                  size="small"
                />
                <span className="text-sm text-muted-foreground">{isOutline ? 'outlines' : 'posts'}/day for</span>
                <InputNumber
                  min={1}
                  max={90}
                  value={numDays}
                  placeholder="5"
                  onChange={(v) => setNumDays(v)}
                  className="w-16"
                  size="small"
                />
                <span className="text-sm text-muted-foreground">days</span>
                {(postsPerDay || numDays) && (
                  <span className="text-xs text-muted-foreground ml-auto">{totalPosts} total</span>
                )}
              </div>
            </div>

            {/* Template */}
            <div className="space-y-1">
              <ParamLabel label="Template" tooltip="Templates define blog structure (headings, sections, CTA). Default lets AI choose the best structure per topic." />
              {templatesLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Spin size="small" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading templates...</span>
                </div>
              ) : (
                <RecipeChooser
                  templates={autoTemplates as unknown as RecipeOption[]}
                  recentlyUsedIds={templates?.recently_used ?? []}
                  recommendedIds={recommendedIds}
                  selectedRecipeId={selectedTemplateId}
                  onSelect={setSelectedTemplateId}
                  onConfirm={(id) => setSelectedTemplateId(id)}
                  selectMode
                  className="p-0"
                />
              )}
            </div>

            {/* Publish Destination */}
            <div className="space-y-1">
              <ParamLabel label="Publish Destination" tooltip="Choose where posts are published on their scheduled date. Draft keeps them in the editor for manual review. WP and Docs publish automatically on schedule." />
              <PublishDestinationSelector
                value={publishDestination}
                onChange={setPublishDestination}
              />
            </div>

          </>
        )}

        {/* ── Keyword Research & Selection (both modes) ──────────── */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <ParamLabel
              label="Keyword Research & Selection"
              tooltip="Select keyword clusters from your completed research sessions. Each cluster generates one blog post targeting that topic."
            />
            <Button
              size="small"
              icon={<Database className="w-3.5 h-3.5" />}
              onClick={() => setShowResearchDrawer(true)}
              className="!text-teal !border-teal/30 hover:!bg-teal-light"
            >
              View Research Sessions
            </Button>
          </div>

          {completedMaps.length === 0 ? (
            <div className="text-center py-4 text-xs text-muted-foreground border rounded-lg border-dashed">
              No research sessions yet.{' '}
              <button
                type="button"
                onClick={() => setShowResearchDrawer(true)}
                className="text-teal hover:underline"
              >
                Start keyword research
              </button>{' '}
              to select clusters for blog post generation.
            </div>
          ) : (
            <div className="max-h-[300px] overflow-y-auto rounded-lg border border-border">
              {completedMaps.map(map => (
                <MapSessionRow
                  key={map.map_id}
                  mapId={map.map_id}
                  seedKeyword={map.seed_keyword}
                  totalClusters={map.total_clusters}
                  totalKeywords={map.total_keywords}
                  totalVolume={map.total_volume}
                  isExpanded={expandedMapId === map.map_id}
                  onToggleExpand={() =>
                    setExpandedMapId(prev => prev === map.map_id ? null : map.map_id)
                  }
                  selectedIds={selectedClusterIds}
                  onToggleCluster={handleToggleCluster}
                  onSelectAllInMap={handleSelectAllInMap}
                  onDeselectAllInMap={handleDeselectAllInMap}
                />
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Expand a research session to select keyword clusters. Each cluster generates one {isOutline ? 'outline' : 'blog post'}.
          </p>
        </div>
      </div>
    </Modal>

    <ResearchSessionsDrawer
      open={showResearchDrawer}
      onOpenChange={(drawerOpen) => {
        setShowResearchDrawer(drawerOpen);
        if (!drawerOpen) {
          refetchMaps();
          queryClient.invalidateQueries({ queryKey: ['topical-map'] });
        }
      }}
      allMaps={allMaps}
      tenantId={tenantId || ''}
      onAddKeyword={() => {}}
      onAddKeywords={() => {}}
      currentKeywordsSet={new Set()}
      refetchMaps={refetchMaps}
      brandBible={brandBible || null}
    />
    </>
  );
}

export default TemplateSelectionModal;
